import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Col, Form, Row, Tag } from 'antd';
import { cloneDeep } from 'es-toolkit';

import { ReactComponent as CloseIcon } from '../../../../../assets/icons/close-white.svg';
import { getStyleVariableValue } from '../../../../../utilz';
import Button from '../../../../components/Buttons/Button';
import SvgConverter from '../../../../components/Common/SvgConverter';
import FormCheckBox from '../../../../components/Inputs/FormCheckBox';
import FormInput from '../../../../components/Inputs/FormInput';
import HiddenFormInput from '../../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../../components/Labels/FormLabel';
import PrimaryLabel from '../../../../components/Labels/PrimaryLabel';
import ContentSpinner from '../../../../components/Spinners/ContentSpinner';
import { useGetLADClaimFilesForEmail, useGetLADCustomerSettings } from '../../hooks';
import { useGetCarrierInfo, useGetClaimDetails } from '../hooks';

const LDSendToCarrierForm = ({ setSendEmailModal = () => {}, handleSendEmailToCarrier = () => {} }) => {
  const [form] = Form.useForm();
  const toEmails = Form.useWatch('toEmails', form);
  const [files, setFiles] = useState([]);
  const { selectedCompany } = useSelector((state) => state.auth);
  const [isLoadEmailsLoading, setIsLoadEmailsLoading] = useState(false);

  const selectedClaimKey = useSelector((state) => state.lossAndDamage.selectedClaimKey);
  const { data: claimDetails } = useGetClaimDetails(selectedClaimKey);
  const { data: customerSettings, isLoading: isCustomerSettingsLoading } = useGetLADCustomerSettings(
    selectedCompany?.companyId,
  );
  const { data: carrierInfo } = useGetCarrierInfo(claimDetails?.carrierKey);
  const {
    data: filesForEmail = [],
    isLoading: isFilesLoading,
    isSuccess: isFilesSuccess,
  } = useGetLADClaimFilesForEmail({ ClaimKey: claimDetails?.claimKey });
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isFilesSuccess) {
      setFiles(filesForEmail);
    } else {
      setFiles([]);
    }
  }, [filesForEmail, isFilesSuccess]);

  const handleAddEmail = async () => {
    try {
      await form.validateFields();
      const newEmail = form.getFieldValue('newEmail');
      const toEmailsL = toEmails ? toEmails : [];
      toEmailsL.push(newEmail);
      form.setFieldsValue({ newEmail: undefined, toEmails: toEmailsL });
    } catch (error) {
      console.log(error);
    }
  };

  const handleRemoveEmail = (email) => {
    form.setFieldsValue({
      toEmails: toEmails?.filter((rowEmail) => rowEmail !== email),
    });
  };

  const handleFileChange = (index) => (e) => {
    const filesL = cloneDeep(files);
    filesL[index].isAttached = e.target.checked;
    setFiles(filesL);
  };

  const handleAllFileChange = (e) => {
    const isSelectedAll = e.target.checked;
    const filesL = files.map((file) => ({ ...file, isAttached: isSelectedAll }));
    setFiles(filesL);
  };

  const handleCancel = () => {
    form.resetFields();
    setFiles([]);
    setSendEmailModal(false);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: fix this
  useEffect(() => {
    handleInitiateEmailsLoad();
  }, []);

  const handleInitiateEmailsLoad = async () => {
    try {
      setIsLoadEmailsLoading(true);
      const toEmails = [];
      if (carrierInfo?.carrierEmail) {
        toEmails.push(carrierInfo.carrierEmail);
      }
      if (user?.data?.email) {
        toEmails.push(user?.data?.email);
      }
      form.setFieldsValue({ toEmails });
      setIsLoadEmailsLoading(false);
    } catch (error) {
      setIsLoadEmailsLoading(false);
      console.log(error);
    }
  };

  const handleSubmit = () => {
    const payload = {
      attachments: files,
      claimKey: claimDetails?.claimKey,
      fromEmail: customerSettings?.fromEmailNotificationId,
      toEmail: toEmails.join(','),
    };
    handleSendEmailToCarrier(payload);
  };

  const { isSelectedAll, isIntermediate } = useMemo(() => {
    const data = { isIntermediate: false, isSelectedAll: false };
    const attachedFiles = files.filter(({ isAttached }) => isAttached);
    data.isSelectedAll = files.length > 0 && attachedFiles.length === files.length;
    data.isIntermediate = attachedFiles.length > 0;
    return data;
  }, [files]);

  return isCustomerSettingsLoading || isFilesLoading || isLoadEmailsLoading ? (
    <ContentSpinner />
  ) : (
    <Form
      className="h-100 overflow-hidden d-flex flex-direction-column"
      form={form}
      initialValues={{ fromEmail: customerSettings?.fromEmailNotificationId, toEmails: [] }}
    >
      <div>
        <div>
          <HiddenFormInput name="toEmails" />
          <FormInput name="fromEmail" label="From" editMode={false} />
        </div>
        <FormLabel>To</FormLabel>
        <Row className="mb-10">
          {toEmails?.map((email) => (
            <Col key={email} className="mb-5">
              <Tag bordered color={getStyleVariableValue('--header')}>
                <Row align="middle" gutter={5}>
                  <Col>
                    <PrimaryLabel className="text-white fw-400">{email}</PrimaryLabel>
                  </Col>
                  <Col>
                    <span className="cursor-pointer">
                      <SvgConverter
                        onClick={() => handleRemoveEmail(email)}
                        onKeyDown={() => handleRemoveEmail(email)}
                        Icon={CloseIcon}
                        color="white"
                        width={13}
                        height={13}
                      />
                    </span>
                  </Col>
                </Row>
              </Tag>
            </Col>
          ))}
        </Row>
        <Row gutter={15}>
          <Col className="flex-1">
            <FormInput
              margin="m-0"
              name="newEmail"
              label={false}
              editMode
              placeholder="Enter new Email"
              inputType="email"
              validateTrigger={['onChange', 'onBlur']}
              rules={[
                { message: 'Please enter email!', required: true },
                {
                  message: 'The input is not valid E-mail!',
                  type: 'email',
                },
                () => ({
                  validator(_, value) {
                    if (toEmails?.includes(value)) {
                      return Promise.reject(new Error('The entered email is already exist in the above list!'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            />
          </Col>
          <Col>
            <Button variant="success" onClick={handleAddEmail}>
              Add
            </Button>
          </Col>
        </Row>
      </div>
      <div className="mb-30 mt-15 flex-1 overflow-hidden d-flex flex-direction-column">
        <FormCheckBox
          checkBoxClass="normal-checkbox"
          className="mb-10"
          label={<PrimaryLabel>Attachments</PrimaryLabel>}
          checked={isSelectedAll}
          indeterminate={isSelectedAll ? false : isIntermediate}
          onChange={handleAllFileChange}
        />
        <div className="flex-1 custom-scroll-bar">
          {files.map((file, index) => (
            <div key={file.sequence}>
              <FormCheckBox
                checkBoxClass="normal-checkbox"
                label={file.fileName}
                className="mb-10"
                checked={file.isAttached}
                onChange={handleFileChange(index)}
              />
            </div>
          ))}
        </div>
      </div>
      <Row justify="end">
        <Col>
          <Row gutter={15}>
            <Col>
              <Button disabled={toEmails?.length === 0} onClick={handleSubmit}>
                Send
              </Button>
            </Col>
            <Col>
              <Button type="default" variant="danger" onClick={handleCancel}>
                Cancel
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default LDSendToCarrierForm;
